// 📄 pages/Admin/sections/KnownErrorsCatalogSection.tsx
import { useEffect, useMemo, useState } from 'react';
import { Plus, RefreshCw, AlertOctagon, Pencil, Trash2, Search } from 'lucide-react';
import {
  KnownError, getAllKnownErrors, deleteKnownError, matchesSearch,
  KNOWN_ERROR_MODULES, KNOWN_ERROR_SOLUCIONES_POR_MODULO, sistemaAfectadoMeta
} from '../../../services/knownErrorsService.ts';
import KnownErrorFormModal from '../components/KnownErrorFormModal.tsx';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';
import { formatFechaVerificacion } from '../../../utils/formatDate.ts';

export default function KnownErrorsCatalogSection() {
  const [errors, setErrors] = useState<KnownError[]>([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroModulo, setFiltroModulo] = useState('');
  const [filtroSolucion, setFiltroSolucion] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingError, setEditingError] = useState<KnownError | null>(null);
  const [errorAEliminar, setErrorAEliminar] = useState<KnownError | null>(null);
  const [eliminando, setEliminando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const data = await getAllKnownErrors();
      setErrors(data);
    } catch (err) {
      console.error('Error cargando el catálogo de errores:', err);
      setError('No se pudo cargar el catálogo de errores.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  // Cambiar el filtro de módulo invalida el de solución (pertenece al
  // submenú del módulo anterior) — mismo criterio que el propio formulario.
  const handleFiltroModulo = (value: string) => {
    setFiltroModulo(value);
    setFiltroSolucion('');
  };

  const solucionesDelFiltro = filtroModulo ? (KNOWN_ERROR_SOLUCIONES_POR_MODULO[filtroModulo] || []) : [];

  const filtrados = useMemo(() => {
    return errors.filter((e) => {
      if (filtroModulo && e.module !== filtroModulo) return false;
      if (filtroSolucion && e.solucion !== filtroSolucion) return false;
      return matchesSearch(e, busqueda);
    });
  }, [errors, busqueda, filtroModulo, filtroSolucion]);

  const openCreate = () => { setEditingError(null); setModalOpen(true); };
  const openEdit = (err: KnownError) => { setEditingError(err); setModalOpen(true); };
  const handleSaved = () => { setModalOpen(false); cargar(); };

  const handleEliminar = async () => {
    if (!errorAEliminar) return;
    setEliminando(true);
    try {
      await deleteKnownError(errorAEliminar.id);
      setErrorAEliminar(null);
      cargar();
    } catch (err) {
      console.error('Error al eliminar el error:', err);
    } finally {
      setEliminando(false);
    }
  };

  const moduloLabel = (id: string) => KNOWN_ERROR_MODULES.find(m => m.id === id)?.label || id;
  const solucionLabel = (moduleId: string, solucionId?: string) => {
    if (!solucionId) return null;
    return (KNOWN_ERROR_SOLUCIONES_POR_MODULO[moduleId] || []).find(s => s.id === solucionId)?.label || solucionId;
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-light text-white">Errores Conocidos</h1>
          <p className="text-sm text-gray-400 mt-1">Catálogo de errores comunes por módulo, visible en modo solo lectura desde cada vista pública (SAP Ariba, SAP BTP, ERP).</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 px-4 py-2.5 rounded-xl text-sm font-semibold text-[#fff] transition-all shadow-lg"
        >
          <Plus className="w-4 h-4" />
          Nuevo error
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="relative sm:col-span-1">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por código, título o tag..."
            className={`${inputClass} pl-9`}
          />
        </div>
        <select value={filtroModulo} onChange={(e) => handleFiltroModulo(e.target.value)} className={inputClass}>
          <option value="">Todos los módulos</option>
          {KNOWN_ERROR_MODULES.map(m => (
            <option key={m.id} value={m.id}>{m.label}</option>
          ))}
        </select>
        <select
          value={filtroSolucion}
          onChange={(e) => setFiltroSolucion(e.target.value)}
          disabled={!filtroModulo}
          className={`${inputClass} disabled:opacity-40`}
        >
          <option value="">{filtroModulo ? 'Todas las soluciones/canales' : 'Elegí un módulo primero'}</option>
          {solucionesDelFiltro.map(s => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
          <p className="text-sm">Cargando catálogo...</p>
        </div>
      ) : filtrados.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <AlertOctagon className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">
            {errors.length === 0 ? 'Aún no hay errores registrados.' : 'Ningún error coincide con la búsqueda/filtros.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtrados.map((err) => (
            <div
              key={err.id}
              className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-2xl p-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-mono font-semibold text-blue-400">{err.code}</span>
                  <span className="text-sm font-medium text-white truncate">{err.title}</span>
                  <span className="text-[10px] bg-white/5 text-gray-400 border border-white/10 px-2 py-0.5 rounded-full">{moduloLabel(err.module)}</span>
                  {solucionLabel(err.module, err.solucion) && (
                    <span className="text-[10px] bg-indigo-500/10 text-indigo-300 border border-indigo-500/30 px-2 py-0.5 rounded-full">{solucionLabel(err.module, err.solucion)}</span>
                  )}
                  {sistemaAfectadoMeta(err.sistemaAfectado) && (
                    <span className="text-[10px] bg-sky-500/10 text-sky-300 border border-sky-500/30 px-2 py-0.5 rounded-full">{sistemaAfectadoMeta(err.sistemaAfectado)!.label}</span>
                  )}
                  {err.activo === false && (
                    <span className="text-[10px] bg-red-500/10 text-red-400 border border-red-500/30 px-2 py-0.5 rounded-full">Inactivo</span>
                  )}
                </div>
                {err.summary && <p className="text-xs text-gray-400 mt-1 line-clamp-1">{err.summary}</p>}
                {err.fechaVerificacion?.toDate && (() => {
                  const { texto, vencida } = formatFechaVerificacion(err.fechaVerificacion.toDate());
                  return (
                    <p className={`text-[11px] mt-1 ${vencida ? 'text-amber-400' : 'text-gray-500'}`}>
                      Verificado el {texto}
                    </p>
                  );
                })()}
              </div>
              <button
                onClick={() => openEdit(err)}
                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all flex-shrink-0"
                title="Editar"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => setErrorAEliminar(err)}
                className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all flex-shrink-0"
                title="Eliminar"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <KnownErrorFormModal
          defaultModule={filtroModulo || 'ariba'}
          error={editingError}
          onClose={() => setModalOpen(false)}
          onSaved={handleSaved}
        />
      )}

      {errorAEliminar && (
        <ConfirmDialog
          title="¿Eliminar este error del catálogo?"
          message={`"${errorAEliminar.code} — ${errorAEliminar.title}" se eliminará del catálogo. Esta acción no se puede deshacer.`}
          confirmLabel={eliminando ? 'Eliminando...' : 'Eliminar'}
          danger
          onConfirm={handleEliminar}
          onCancel={() => setErrorAEliminar(null)}
        />
      )}
    </div>
  );
}
